import React, { useState } from 'react';
import { Empty, message } from 'antd';
import { GlobalOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons';
import PaginationTable from '../../../shared/PaginationTable';
import { getRegionGroupColumns } from '../RegionListColumn.js';
import { useBreakpoints } from '../../../shared/globalStyling/screenSizes';
import { pagination } from '../../../ux/Tasks/configs/designTokens';

// ─── Expanded row: region pills (matches Audiences location pills) ──
const ExpandedRegions = ({ record }) => {
  if (!record.regions || !record.regions.length) {
    return <div className="targeting-region__expand-empty">No regions</div>;
  }

  return (
    <div className="targeting-region__expand">
      {record.regions.map((region, index) => (
        <div key={index} className="targeting-region__expand-item">
          <GlobalOutlined className="targeting-region__expand-icon" />
          <span className="targeting-region__expand-name">{region}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Card view (tablet / mobile) ────────────────────────────────────────
const RegionGroupCardList = ({
  data,
  selectedKeys,
  onSelect,
  onEdit,
  onCopy,
  isEditDisabled,
  expandedCardId,
  onCardExpand,
}) => (
  <div className="region-card-list">
    {data.map((record) => {
      const isSelected = selectedKeys.includes(record.id);
      const isExpanded = expandedCardId === record.id;
      const regionCount = record.regions ? record.regions.length : 0;

      return (
        <div key={record.id} className={`region-card-wrap${isSelected ? ' region-card-wrap--selected' : ''}`}>
          <div
            className="region-card"
            onClick={() => onSelect(record.id)}
          >
            <div className="region-card__header">
              <span className="region-card__name">{record.name}</span>
              <div className="region-card__actions">
                <button
                  type="button"
                  className="region-card__action-btn"
                  onClick={(e) => { e.stopPropagation(); onCopy(record); }}
                >
                  <CopyOutlined />
                </button>
                <button
                  type="button"
                  className={`region-card__action-btn${isEditDisabled ? ' region-card__action-btn--disabled' : ''}`}
                  onClick={(e) => { e.stopPropagation(); if (!isEditDisabled) onEdit(record); }}
                  disabled={isEditDisabled}
                >
                  <EditOutlined />
                </button>
              </div>
            </div>
            <div className="region-card__meta">
              <span className="region-card__region-badge">
                <GlobalOutlined /> {regionCount} region{regionCount !== 1 ? 's' : ''}
              </span>
              {record.isPresetList && (
                <span className="region-card__preset-badge">Preset</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="region-card__expand"
            onClick={() => onCardExpand(isExpanded ? null : record.id)}
          >
            {isExpanded ? 'Hide regions' : `View ${regionCount} region${regionCount !== 1 ? 's' : ''}`}
            <span className={`region-card__chevron${isExpanded ? ' region-card__chevron--open' : ''}`}>
              &#9662;
            </span>
          </button>
          {isExpanded && <ExpandedRegions record={record} />}
        </div>
      );
    })}
  </div>
);

export default function RegionGroupListView({
  regionGroups: { regionGroups },
  toggleForm,
  setCurrentRegion,
  handleSubmitRegionGroup,
  selectedOrg,
  currentuser,
  searchText,
  setSelectedRows,
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pagination.defaultPageSize);
  const [selectedCardKeys, setSelectedCardKeys] = useState([]);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const { isTablet } = useBreakpoints();

  const handleEdit = (region) => {
    setCurrentRegion(region);
    toggleForm(true);
  };

  const handleCopy = async (val) => {
    message.loading('Action in progress...', 0);
    let payload = {
      name: 'Copy of ' + val.name,
      regions: val.regions,
      isPresetList: val.isPresetList,
    };

    await handleSubmitRegionGroup(payload)
      .then(() => {
        message.destroy();
        message.success(`Successfully cloned Region Group`);
      })
      .catch(() => {
        message.destroy();
        message.error('Something went wrong');
      });
  };

  const columns = getRegionGroupColumns(
    handleEdit,
    handleCopy,
    currentuser,
    selectedOrg
  );

  const filteredData = regionGroups
    ? regionGroups
        .filter((rg) =>
          searchText
            ? (rg.name || '').toLowerCase().includes(searchText.toLowerCase())
            : true
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : [];

  const expandedRowRender = (record) => <ExpandedRegions record={record} />;

  const isEditDisabled = currentuser.role.org.name !== selectedOrg.name;

  const handleCardSelect = (id) => {
    setSelectedCardKeys((prev) => {
      const next = prev.includes(id)
        ? prev.filter((k) => k !== id)
        : [...prev, id];
      const selectedRows = regionGroups.filter((rg) => next.includes(rg.id));
      setSelectedRows(selectedRows);
      return next;
    });
  };

  if (!regionGroups || !regionGroups.length) {
    return <Empty />;
  }

  return isTablet ? (
    <RegionGroupCardList
      data={filteredData}
      selectedKeys={selectedCardKeys}
      onSelect={handleCardSelect}
      onEdit={handleEdit}
      onCopy={handleCopy}
      isEditDisabled={isEditDisabled}
      expandedCardId={expandedCardId}
      onCardExpand={setExpandedCardId}
    />
  ) : (
    <div className="targeting-region__table">
      <PaginationTable
        columns={columns}
        data={filteredData}
        scroll={{ x: 500 }}
        bordered={false}
        paginationSize={'default'}
        size={'small'}
        current={page}
        pageSize={pageSize}
        total={filteredData.length}
        rowSelection={{
          type: 'checkbox',
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows);
          },
        }}
        onChange={(paginationObject) => {
          setPageSize(paginationObject.pageSize);
          setPage(paginationObject.current);
        }}
        rowKey="id"
        expandedRowRender={expandedRowRender}
      />
    </div>
  );
}
